import { useEffect, useState } from "react";

// STYLES
import * as Style from "./LockerDetailsStyle";

// LIBRARIES
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

// MISC
import { COLORS } from "constants/styleVariables";
import {
  ButtonTypes,
  ChildModalTypes,
  InputTypes,
  lockerCurencies,
  ModalTypes
} from "models/GeneralTypes/generalTypes";
import { ILockerDetails, ISlot } from "models/interfaces";

// ASSETS
import DownArrowIcon from "assets/Icons/DownArrowIcon.svg";
import LeftIcon from "assets/Icons/LeftBlueIcon.svg";
import InputAtom from "components/Atoms/InputAtom/InputAtom";
import TimePickerAtom from "components/Atoms/TimePickerAtom/TimePickerAtom";

// REDUX
import { editLocker, getLocker, getLockerSlots } from "api/lockerApi";
import { IModalData } from "components/Atoms/ModalAtom/ModalAtomModel";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";
import { lockerFace, modalProps, toggleLockerFace, toggleModalState } from "slices/uiSlice";

// COMPONENTS
import ButtonAtom from "components/Atoms/ButtonAtom/ButtonAtom";
import DoorDetails from "components/Atoms/ModalAtom/Content/DoorDetails/DoorDetails";
import SelectAtom from "components/Atoms/SelectAtom/SelectAtom";


const LockerDetails = () => {
  // PROPS

  // CONSTANTS USING LIBRARIES
  const dispatch = useAppDispatch();
  const routeParams = useParams();
  const { t } = useTranslation();
  const lockerFaceData = useAppSelector<string>(lockerFace);
  const modalPropsData = useAppSelector<IModalData>(modalProps);

  // CONSTANTS USING HOOKS
  const [openDetails, setOpenDetails] = useState<boolean>(false);
  const [lockerDetailsValues, setLockerDetailsValues] =
    useState<ILockerDetails>({
      longitude: "",
      latitude: "",
      name: "",
      address: "",
      price: "",
      noOfAvailableSlots: "",
      noOfAvailableSupBoards: "",
      noOfSlots: "",
      noOfSupBoards: "",
      startHour: "",
      endHour: "",
      status: "",
    });
  const [frontSlotDetails, setFrontSlotDetails] = useState<ISlot[]>();
  const [backSlotDetails, setBackSlotDetails] = useState<ISlot[]>();
  const [selectedCurrency, setSelectedCurrency] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [screenWidth, setScreenWidth] = useState(0);

  // GENERAL CONSTANTS
  const { lockerId } = routeParams;

  // USE EFFECT FUNCTION
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    handleResize(); // Apelați funcția handleResize inițial pentru a seta valoarea inițială corectă

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    setIsLoading(true);
    dispatch(getLocker(lockerId))
      .unwrap()
      .then((response) => {
        let lockerDetails = {
          ...response,
          startHour: dayjs(response.startHour, "HH:mm:ss"),
          endHour: dayjs(response.endHour, "HH:mm:ss"),
        };
        setLockerDetailsValues(lockerDetails);
        dispatch(getLockerSlots(lockerId))
          .unwrap()
          .then((response) => {
            setFrontSlotDetails(response.slice(0, 8));
            setBackSlotDetails(response.slice(8));
            setIsLoading(false);
          });
        setIsLoading(false);
      });
    // eslint-disable-next-line
  }, []);

  // REQUEST API FUNCTIONS

  // HANDLERS FUNCTIONS
  const openPowerUpModal = () => {
    dispatch(
      toggleModalState({
        isVisible: true,
        type: ChildModalTypes.POWER_UP,
      })
    );
  }

  const handleOpen = (door) => {
    // Always show modal on screens smaller than 1300px (including mobile)
    dispatch(
      toggleModalState({
        ...modalPropsData,
        isVisible: screenWidth < 1300,
        type: ModalTypes.DOOR,
        door: door,
        lockerId: lockerId
      })
    );
  };

  const handleSlotClick = (slotNumber) => {
    const selectedSlot = frontSlotDetails?.find(slot => slot.identificationNumber === slotNumber);

    if (selectedSlot) {
      handleOpen(selectedSlot);
    }
  };

  // Helper function to get Romanian status text
  const getStatusText = (status) => {
    switch (status) {
      case 'AVAILABLE':
        return 'Disponibil';
      case 'RENTED':
        return 'Închiriat';
      case 'UNAVAILABLE':
        return 'Indisponibil';
      default:
        return 'Slot';
    }
  };

  const handleCloseDoorDetail = () => {
    dispatch(
      toggleModalState({
        ...modalPropsData,
        isVisible: false,
        type: "",
        door: null
      })
    );
  };

  const handleOpenDetails = () => {
    setOpenDetails(true);
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
  };

  const handleMaintenanceStatus = () => {
    let lockerDetailsData = {
      ...lockerDetailsValues,
      startHour: dayjs(lockerDetailsValues.startHour).format("HH:mm:ss"),
      endHour: dayjs(lockerDetailsValues.endHour).format("HH:mm:ss"),
      status:
        lockerDetailsValues.status === "WORKING" ? "NOT_WORKING" : "WORKING",
    };
    dispatch(editLocker(lockerDetailsData))
      .unwrap()
      .then(() => {
        dispatch(getLocker(lockerDetailsValues.id))
          .unwrap()
          .then((response) => {
            let lockerDetails = {
              ...response,
              startHour: dayjs(response.startHour, "HH:mm:ss"),
              endHour: dayjs(response.endHour, "HH:mm:ss"),
            };
            setLockerDetailsValues(lockerDetails);
          });
      });
  };

  return (
    <Style.Container>
      {isLoading ?
        <Style.LoadingContainerDetails>
          <Style.LodingSign />
        </Style.LoadingContainerDetails>
        :
        <>
          <Style.ContainerDetails>
            <Style.LeftContentContainerDetails>
              <Style.TitleStatusContainer>
                <Style.ContainerDetailsTitle>
                  {t("component.lockerItem.title")} {lockerDetailsValues.name}
                </Style.ContainerDetailsTitle>
                <Style.ContainerDetailsStatus>
                  {t(
                    `component.lockerFunctionality.locker.status.${lockerDetailsValues.status}`
                  )}
                </Style.ContainerDetailsStatus>
              </Style.TitleStatusContainer>
              <Style.TitleStatusContainer>
                <Style.ContainerDetailsTitle>
                  {t("component.lockerFunctionality.label.lockersAddress")}
                </Style.ContainerDetailsTitle>
                <Style.ContainerDetailsAddress>
                  {lockerDetailsValues.address}
                </Style.ContainerDetailsAddress>
              </Style.TitleStatusContainer>

              {/* <Style.PowerUpIcon onClick={() => openPowerUpModal()} /> */}
              <Style.InformationsContainer>
                <Style.StraightBar />
                <Style.Details>
                  <Style.DetailsTitle>
                    {t("component.lockerFunctionality.label.availableDoors")}
                  </Style.DetailsTitle>
                  <Style.DetailsNumber>
                    {lockerDetailsValues.noOfAvailableSlots} /{" "}
                    {lockerDetailsValues.noOfSlots}
                  </Style.DetailsNumber>
                </Style.Details>
                <Style.StraightBar />
                <Style.Details>
                  <Style.DetailsTitle>
                    {t("component.lockerFunctionality.label.availableBoards")}
                  </Style.DetailsTitle>
                  <Style.DetailsNumber>
                    {lockerDetailsValues.noOfAvailableSupBoards} /{" "}
                    {lockerDetailsValues.noOfSupBoards}
                  </Style.DetailsNumber>
                </Style.Details>
                <Style.StraightBar />
                <Style.Details>
                  <Style.DetailsTitles>
                    {t(
                      "component.lockerFunctionality.locker.label.lockerMaintenance"
                    )}
                  </Style.DetailsTitles>
                  <Style.DetailsNumber>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Style.ToggleSwitch
                            color="default"
                            onChange={handleMaintenanceStatus}
                            checked={
                              lockerDetailsValues.status === "WORKING"
                                ? true
                                : false
                            }
                            colorValue={
                              lockerDetailsValues.status === "WORKING"
                                ? COLORS.rainbowIndigo
                                : COLORS.terraCotta
                            }
                          />
                        }
                        label={
                          <Style.LabelContainer
                            colorValue={
                              lockerDetailsValues.status === "WORKING"
                                ? COLORS.rainbowIndigo
                                : COLORS.terraCotta
                            }
                          >
                            {lockerDetailsValues.status === "WORKING"
                              ? "ON"
                              : "OFF"}
                          </Style.LabelContainer>
                        }
                      />
                    </FormGroup>
                  </Style.DetailsNumber>
                </Style.Details>
              </Style.InformationsContainer>
            </Style.LeftContentContainerDetails>
            {!openDetails && (
              <Style.RotatedArrowIcon
                onClick={handleOpenDetails}
                src={LeftIcon}
                alt=""
              />
            )}
            {openDetails && (
              <Style.DownIconArrowContainer
                onClick={handleCloseDetails}
                src={DownArrowIcon}
                alt=""
              />
            )}
          </Style.ContainerDetails>
          {openDetails && (
            <Style.OpenContainerDetails>
              <Style.OpenContainerDetailsLeftContent>
                <Style.DateInputContainer>
                  <TimePickerAtom
                    label={t("component.lockerFunctionality.label.selectStartTime")}
                    value={lockerDetailsValues.startHour}
                  />
                </Style.DateInputContainer>
                <Style.DateInputContainer>
                  <TimePickerAtom
                    label={t("component.lockerFunctionality.label.selectEndTime")}
                    value={lockerDetailsValues.endHour}
                  />
                </Style.DateInputContainer>
                <Style.InputContainer>
                  <InputAtom
                    placeholder={t(
                      "component.lockerFunctionality.placeholder.latitude"
                    )}
                    label={t("component.lockerFunctionality.label.latitude")}
                    inputValue={lockerDetailsValues.latitude}
                    type={InputTypes.MODAL_INPUT}
                  />
                </Style.InputContainer>
                <Style.InputContainer>
                  <InputAtom
                    placeholder={t(
                      "component.lockerFunctionality.placeholder.longitude"
                    )}
                    label={t("component.lockerFunctionality.label.longitude")}
                    inputValue={lockerDetailsValues.longitude}
                    type={InputTypes.MODAL_INPUT}
                  />
                </Style.InputContainer>
                <Style.InputContainer>
                  <InputAtom
                    label={t("component.lockerFunctionality.label.price")}
                    inputValue={lockerDetailsValues.price}
                    type={InputTypes.MODAL_INPUT}
                  />
                </Style.InputContainer>
                <Style.SelectInputAtomContainer>
                  <SelectAtom
                    options={lockerCurencies}
                    selectedOptions={selectedCurrency}
                    setSelectedOptions={setSelectedCurrency}
                    label={t("component.lockerFunctionality.label.currency")}
                  />
                </Style.SelectInputAtomContainer>
              </Style.OpenContainerDetailsLeftContent>
              <Style.ButtonContainer>
                <ButtonAtom
                  text={t("component.lockerFunctionality.button.title.delete")}
                  type={ButtonTypes.THIRD_BUTTON}
                />
              </Style.ButtonContainer>
            </Style.OpenContainerDetails>
          )}
        </>}

      <Style.LockerContainer>
        <Style.LockerFaceContainer>
          <Style.ImagesContainer>
            <Style.ImageTextContainer>
              <Style.SlotsTitle>Sloturi Disponibile</Style.SlotsTitle>
              <Style.SlotsGrid>
                {Array.from({ length: 8 }, (_, index) => {
                  const slotNumber = index + 1;
                  const slotData = frontSlotDetails?.find(slot => slot.identificationNumber === slotNumber);

                  return (
                    <Style.SlotCard
                      key={slotNumber}
                      status={slotData?.status}
                      onClick={() => handleSlotClick(slotNumber)}
                    >
                      <Style.SlotNumber>{slotNumber}</Style.SlotNumber>
                      <Style.SlotLabel>
                        {slotData ? getStatusText(slotData.status) : "Slot"}
                      </Style.SlotLabel>
                    </Style.SlotCard>
                  );
                })}
              </Style.SlotsGrid>
            </Style.ImageTextContainer>
          </Style.ImagesContainer>
        </Style.LockerFaceContainer>
        {screenWidth > 1300 && <Style.NoDoorDetails>
          {modalPropsData?.door ? <DoorDetails handleClose={handleCloseDoorDetail} /> : "Select slot"}
        </Style.NoDoorDetails>}
      </Style.LockerContainer >
    </Style.Container>
  );
};

export default LockerDetails;
